<template>
</template>

<script>
    import moment from 'moment';
    import _ from 'lodash';
    import draggable from 'vuedraggable';

    export default {
        props: {
            assignment: Object,
            user: Object,
            qaRequest: {
                type: Object,
                default: null
            },
            readOnly: {
                type: Boolean,
                default: false
            },
            idleTimeoutSeconds: {
                type: Number,
                default: 1800,
            },
            idleWarningSeconds: {
                type: Number,
                default: 300,
            },
            csrfToken: {
                type: String,
                default: '',
            },
            surrenderRedirectUrl: {
                type: String,
                default: '/',
            },
        },
        components: {
            draggable,
        },
        data() {
            return {
                hasProblem: false,
                problemComment: '',
                icdSearch: '',
                results: [],
                codes: this.assignment.codes || [],
                // maxId: 0,
                drag: false,
                enoughTime: false,
                loading: false, // currently prevents multiple send to problem clicks
                showReview: false,
                reviewed: false,
                showPoc: false,
                poced: false,
                completing: false,
                // QA Data
                showQa: false,
                qaType: 'question',
                qaMessage: '',
                qaSubmitting: false,
                qaResponse: '',
                // Idle timeout
                lastInteractionAt: Date.now(),
                secondsRemaining: 1800,
                showIdleWarning: false,
                releasedByUs: false,
            }
        },
        computed: {
            lastProblem: function() {
                return this.assignment.problems.slice(-1)[0];
            },
            date: function() {
                return moment.utc(this.assignment.date).format('MMM DD, YYYY');
            },
            timerLabel() {
                const total = Math.max(0, this.secondsRemaining);
                const m = Math.floor(total / 60);
                const s = total % 60;
                return `${m}:${s.toString().padStart(2, '0')}`;
            },
            timerColorClass() {
                if (this.secondsRemaining <= 60) {
                    return 'bg-red-100 text-red-800 border-red-300 animate-pulse';
                }
                if (this.secondsRemaining <= this.idleWarningSeconds) {
                    return 'bg-amber-100 text-amber-800 border-amber-300';
                }
                return 'bg-emerald-50 text-emerald-800 border-emerald-200';
            },
        },
        mounted() {
            setTimeout(()=>{
                this.enoughTime = true;
            }, 3000);

            this.secondsRemaining = this.idleTimeoutSeconds;
            this.recordInteraction();

            // Heartbeat ping so site admins can see coder is actively working.
            // Only pings when there has been real interaction in the last 60s.
            this.sendPing();
            this._pingInterval = setInterval(() => this.maybeSendPing(), 60000);

            // Countdown tick.
            this._tickInterval = setInterval(() => this.tick(), 1000);

            // Interaction listeners reset the timer.
            // Skip ambient interactions inside the idle warning modal — that modal
            // has its own explicit Continue / Return buttons. If we dismissed the
            // modal on mousedown, Vue would unmount the buttons before click landed.
            this._interactionHandler = (e) => {
                if (this.showIdleWarning && e && e.target && e.target.closest && e.target.closest('[data-idle-modal]')) {
                    return;
                }
                this.recordInteraction();
            };
            ['mousedown', 'keydown', 'wheel', 'touchstart'].forEach((evt) => {
                window.addEventListener(evt, this._interactionHandler, { passive: true });
            });
            this._visibilityHandler = () => {
                if (document.visibilityState === 'visible') {
                    this.recordInteraction();
                }
            };
            document.addEventListener('visibilitychange', this._visibilityHandler);

            // Release on tab close / navigation away (best-effort via sendBeacon).
            this._pageHideHandler = () => this.releaseOnExit();
            window.addEventListener('pagehide', this._pageHideHandler);
        },
        beforeDestroy() {
            if (this._pingInterval) clearInterval(this._pingInterval);
            if (this._tickInterval) clearInterval(this._tickInterval);
            if (this._interactionHandler) {
                ['mousedown', 'keydown', 'wheel', 'touchstart'].forEach((evt) => {
                    window.removeEventListener(evt, this._interactionHandler);
                });
            }
            if (this._visibilityHandler) {
                document.removeEventListener('visibilitychange', this._visibilityHandler);
            }
            if (this._pageHideHandler) {
                window.removeEventListener('pagehide', this._pageHideHandler);
            }
        },
        methods: {
            complete() {
                if (this.assignment.review) {
                    if(!this.reviewed) {
                        this.showReview = true;
                        return;
                    }
                }

                if (this.assignment.poc) {
                    if(!this.poced) {
                        this.showReview = false;
                        this.showPoc = true;
                        return;
                    }
                }

                this.releasedByUs = true;
                this.completing = true;
                axios.patch('/assignments/'+this.assignment.id+'/complete')
                    .then(({data}) => {
                        this.next();
                    })
                    .catch(() => {
                        this.completing = false;
                        this.$toast.error('An error occurred while completing your assignment.');
                    });
            },
            review() {
                this.reviewed = true;
                this.complete();
            },
            poc() {
                this.poced = true;
                this.complete();
            },
            toggleProblems() {
                this.hasProblem = !this.hasProblem;
            },
            cancelProblem() {
                this.toggleProblems();
            },
            sendToProblems() {
                this.releasedByUs = true;
                this.completing = true;
                this.loading = true;
                axios.patch('/assignments/'+this.assignment.id+'/problem', {comment: this.problemComment})
                    .then(({data}) => {
                        this.next();
                    })
                    .catch(() => {
                        this.completing = false;
                        this.$toast.error('An error occurred while sending to problems.');
                    });
            },
            next() {
                location.reload();
            },
            surrender(e) {
                if(this.lastProblem && this.lastProblem.user_id == this.user.id) {
                    let confirmation = confirm('Are you sure you want to return this document? You will not get credit for this assignment if you do.');

                    if(confirmation) {
                        this.releasedByUs = true;
                        return true;
                    }

                    if (e && typeof e.preventDefault === 'function') {
                        e.preventDefault();
                    }
                    return false;
                }
                this.releasedByUs = true;
            },
            recordInteraction() {
                this.lastInteractionAt = Date.now();
                this.secondsRemaining = this.idleTimeoutSeconds;
                if (this.showIdleWarning) {
                    this.showIdleWarning = false;
                }
            },
            refreshTimer() {
                this.recordInteraction();
                this.sendPing();
            },
            tick() {
                if (this.completing || this.releasedByUs) return;
                const elapsed = Math.floor((Date.now() - this.lastInteractionAt) / 1000);
                this.secondsRemaining = Math.max(0, this.idleTimeoutSeconds - elapsed);
                if (this.secondsRemaining <= this.idleWarningSeconds && !this.showIdleWarning) {
                    this.showIdleWarning = true;
                }
                if (this.secondsRemaining <= 0) {
                    this.surrenderAndRedirect();
                }
            },
            surrenderAndRedirect() {
                if (this.releasedByUs) return;
                this.releasedByUs = true;
                if (this._tickInterval) clearInterval(this._tickInterval);
                if (this._pingInterval) clearInterval(this._pingInterval);
                axios.patch('/assignments/' + this.assignment.id + '/surrender')
                    .finally(() => {
                        window.location.href = this.surrenderRedirectUrl;
                    });
            },
            maybeSendPing() {
                const idleMs = Date.now() - this.lastInteractionAt;
                if (idleMs <= 60000) {
                    this.sendPing();
                }
            },
            releaseOnExit() {
                if (this.releasedByUs || this.completing) return;
                if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;
                const form = new FormData();
                form.append('_method', 'PATCH');
                if (this.csrfToken) {
                    form.append('_token', this.csrfToken);
                }
                navigator.sendBeacon('/assignments/' + this.assignment.id + '/surrender', form);
            },
            search: _.debounce(function() {
                if (this.icdSearch.length <= 2) {
                    this.results = [];
                    return;
                }
                if (this.icdSearch.indexOf('.') != -1) {
                    var url = 'https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search?sf=code,name&terms=';

                    axios.get(url+ this.icdSearch)
                    .then(({data}) => {
                        this.results = [];
                        data[3].forEach(element => {
                            this.results.push({code: element[0], text: element[1]});
                        });
                    });
                } else {
                    var url = 'https://icd.hhcoder.com/api?search=';
                    axios.get(url+ this.icdSearch)
                    .then(({data}) => {
                        this.results = [];
                        data.forEach(element => {
                            var code = element.code;
                            code = code.slice(0,3) + '.'+ code.slice(3);
                            this.results.push({code: code, text: element.description});
                        });
                    });
                }
            }, 250),
            addCode (code) {
                if (this.codes.includes(code) == false) {
                    this.codes.push(code);
                    this.updateCodes('Code Added');
                } else {
                    alert('Code Already Applied');
                }
            },
            moveCode() {
                this.updateCodes('Code Moved');
            },
            deleteCode(code) {
                this.codes.splice(this.codes.indexOf(code), 1);
                this.updateCodes('Code Removed');
            },
            updateCodes(message=null) {
                axios.patch(`/assignments/${this.assignment.id}/update`, {
                    field: 'codes',
                    codes: this.codes
                }).then(({data}) => {
                    message = message || 'Successfully updated the codes';
                    this.$toast.info(message, {timeout: 2000});
                });
            },
            updateComment(updatedAssignment) {
                this.assignment.coder_comment = updatedAssignment.coder_comment;
            },
            submitQa() {
                if (this.qaType == 'question' && !this.qaMessage) {
                     this.$toast.error('Please enter a message.');
                     return;
                }
                this.qaSubmitting = true;
                axios.post('/coders/qa', {
                    assignment_id: this.assignment.id,
                    type: this.qaType,
                    message: this.qaMessage
                }).then(({data}) => {
                    this.showQa = false;
                    this.qaSubmitting = false;
                    this.qaMessage = '';
                    this.$toast.success('Request sent successfully!');
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                }).catch((err) => {
                    this.qaSubmitting = false;
                    this.$toast.error('Failed to send request.');
                });
            },
            sendPing() {
                axios.patch('/assignments/' + this.assignment.id + '/ping').catch(() => {});
            },
            submitQaResponse() {
                if (!this.qaResponse) {
                    this.$toast.error('Please enter a response.');
                    return;
                }
                axios.patch('/coders/qa/'+this.qaRequest.id, {
                    action: 'respond',
                    response: this.qaResponse
                }).then(() => {
                    this.$toast.success('Response sent!');
                    window.location.href = '/coders/qa';
                });
            }
        }
    }
</script>
